import { map, some, filter, intersection, each, find } from 'lodash';
/**
 * @ngdoc directive
 * @name adjust.statistics.directive:columns
 *
 * @description
 * Column selection directive
 *
 */
function columns() {
  'ngInject';
  return {
    restrict: 'E',
    scope: {},
    template: require('./columns.html'),
    controller: 'ColumnsController',
    controllerAs: 'vm',
    bindToController: {
      show: '=',
      ngModel: '=',
      attributionType: '=',
      attributionSource: '=',
      loadingState: '=',
      subLevel: '=',
      period: '=',
      base: '=',
      onClose: '&',
      grouping: '=',
      trackerToken: '=',
    },
  };
}

/**
 * @ngdoc controller
 * @name adjust.statistics.controller:ColumnsController
 *
 * @description
 * Controller for the {@link adjust.statistics.directive:columns columns} directive
 *
 */
function ColumnsController(Column, Kpi, Helper, $scope, $filter, $q) {
  'ngInject';
  var vm = this;

  vm.selectedFilteredMsg = false;
  vm.deselectedFilteredMsg = false;
  vm.allSelected = false;

  vm.apply = apply;
  vm.toggleFiltered = toggleFiltered;
  vm.toggleAll = toggleAll;
  vm.toggleGroup = toggleGroup;
  vm.handleSearchChange = handleSearchChange;

  $scope.$watch('vm.loadingState', _handleLoadingChange);
  $scope.$watch('vm.show', _handleVisibility);
  $scope.$on('$destroy', _handleDestroy);

  /**
   * @ngdoc method
   * @name _handleLoadingChange
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Handle loading state change callback
   *
   * @param {boolean} loading loading state
   * @private
   */
  function _handleLoadingChange(loading) {
    vm.loading = loading;

    if (loading) {
      return;
    }

    _prepareColumns();
  }

  /**
   * @ngdoc method
   * @name _handleVisibility
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Handle columns section visibility and prepare columns if shown
   *
   * @param {boolean} n old state
   * @param {boolean} o new state
   * @private
   */
  function _handleVisibility(n, o) {
    if (n !== o && n) {
      _prepareColumns();
    }
  }

  /**
   * @ngdoc method
   * @name _prepareColumns
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Prepare columns list
   *
   * @private
   */
  function _prepareColumns() {
    var persistentColumns = Column.getPersistentColumns('cohorts', vm.base);

    return $q
      .all([
        Kpi.getAll({
          attributionType: vm.attributionType,
          attributionSource: vm.attributionSource,
          grouping: vm.grouping,
          trackerToken: vm.trackerToken,
          type: 'cohorts',
        }),
        Column.getColumns({
          attributionType: vm.attributionType,
          attributionSource: vm.attributionSource,
          tmpChange: true,
        }),
      ])
      .then(function(results) {
        var [kpis, columnItems] = results;
        var selectedColumns = columnItems.columns;
        var finalList = kpis[vm.subLevel];

        vm.columns = map(finalList, function(event) {
          var name = event.originalName || event.name;
          var label = Helper.translateKpi(name, vm.period);
          return {
            name: name,
            group: event.group || null,
            token: event.token,
            isGroup: event.isGroup || false,
            isIndented: some(finalList, { isGroup: true }) ? !event.isGroup : false,
            hide: event.hide,
            label: label,
            selected:
              selectedColumns && selectedColumns.length
                ? _isSelected(selectedColumns, event, finalList)
                : persistentColumns.includes(event.token),
          };
        });
      });
  }

  /**
   * Check if element is selected for display
   *
   * @param {Array} selectedColumns
   * @param {Object} column
   * @param {Array} list
   * @returns {Boolean}
   * @private
   */
  function _isSelected(selectedColumns, column, list) {
    if (!column.isGroup) {
      return selectedColumns.includes(column.token);
    }

    var group = map(filter(list, { group: column.token }), 'token');
    var selectedIntersection = intersection(selectedColumns, group);

    return selectedIntersection.length === group.length;
  }

  /**
   * @ngdoc method
   * @name _filterSelected
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Get selected columns
   *
   * @returns {Array} list of selected columns
   * @private
   */
  function _filterSelected() {
    return Column.filterSelected(vm.columns, 'isGroup');
  }

  /**
   * Get columns after applying a filter
   *
   * @returns {Array}
   * @private
   */
  function _getFiltered() {
    return $filter('filter')(vm.columns, {
      $: vm.searchTerm,
    });
  }

  /**
   * @ngdoc method
   * @name apply
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Apply selected columns on the table
   */
  function apply() {
    var selected = _filterSelected();

    if (!selected.length) {
      vm.error = true;
      return;
    }

    vm.error = false;

    Column.setSelected(selected);

    vm.onClose();
  }

  /**
   * @ngdoc method
   * @name toggleFiltered
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Toggle all filtered checkboxes
   */
  function toggleFiltered() {
    var filteredList = _getFiltered();
    var state = !vm.allSelected;

    vm.allSelected = !vm.allSelected;

    vm.selectedFilteredMsg = filteredList.length !== vm.columns.length && state;
    vm.deselectedFilteredMsg = filteredList.length !== vm.columns.length && !state;

    each(filteredList, function(column) {
      column.selected = state;
    });
  }

  /**
   * @ngdoc method
   * @name toggleAll
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Toggle all checkboxes
   *
   * @param {boolean} state checkboxes new state
   */
  function toggleAll(state) {
    vm.searchTerm = '';
    vm.toggled = state;

    each(vm.columns, function(column) {
      column.selected = state;
    });

    vm.handleSearchChange();
  }

  /**
   * @ngdoc method
   * @name handleSearchChange
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Trigger on change of search term
   */
  function handleSearchChange() {
    vm.selectedFilteredMsg = false;
    vm.deselectedFilteredMsg = false;

    _checkAllSelected();
  }

  /**
   * @ngdoc method
   * @name _checkAllSelected
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Checks if all items in view are selected
   *
   * @private
   */
  function _checkAllSelected() {
    vm.allSelected = Column.isAllSelected(_getFiltered());
  }

  /**
   * @ngdoc method
   * @name toggleGroup
   * @methodOf adjust.statistics.controller:ColumnsController
   *
   * @description
   * Toggle group depending on screen:
   * - corresponding revenue if exists
   * - group of kpis for cohorts
   *
   * @param {Object} column columns that has been toggled
   */
  function toggleGroup(column) {
    var total;
    var selected;
    var group;

    if (column.isGroup) {
      each(filter(vm.columns, { group: column.token }), function(c) {
        c.selected = column.selected;
      });
    } else if (column.group) {
      total = filter(vm.columns, { group: column.group }).length;
      selected = filter(vm.columns, { group: column.group, selected: true }).length;
      group = find(vm.columns, { token: column.group });

      group.selected = total === selected;
    } else {
      return;
    }

    _checkAllSelected();
  }

  /**
   * Clear temporary selected columns
   *
   * @private
   */
  function _handleDestroy() {
    Column.destroySelected();
  }
}

export { ColumnsController, columns };
